import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useUser from '../hooks/useUser'

const RoleBasedRedirect = () => {
  const navigate = useNavigate()
  const { user, roleName, employee, loading } = useUser()

  const getAccessibleDashboard = (userScreens, roleName, department) => {
    // Convert user screens to a Set for quick lookup
    const userScreenIds = new Set((userScreens || []).map(s => s.screen_id))

    // Map of screen codes to their screen_ids (you might need to create this from your screens data)
    const screenCodeToIdMap = {
      MARKETING_DASHBOARD: 37,
      SALES_DASHBOARD: 38,
      OPERATIONS_DASHBOARD: 39,
      FACILITY_DASHBOARD: 40,
      HR_DASHBOARD: 41,
      RENT_SCHEDULER: 24,
    }

    // Check access based on role and department
    if (roleName === 'super-admin') {
      if (userScreenIds.has(screenCodeToIdMap.MARKETING_DASHBOARD)) {
        return '/marketing-dashboard'
      }
    } else if (roleName === 'hr') {
      if (userScreenIds.has(screenCodeToIdMap.HR_DASHBOARD)) {
        return '/hr-dashboard'
      }
    } else if (roleName === 'admin' || roleName === 'location-admin' || roleName === 'user') {
      if (department === 'marketing' && userScreenIds.has(screenCodeToIdMap.MARKETING_DASHBOARD)) {
        return '/marketing-dashboard'
      } else if (
        department === 'operations' &&
        userScreenIds.has(screenCodeToIdMap.OPERATIONS_DASHBOARD)
      ) {
        return '/operations-dashboard'
      } else if (department === 'hr' && userScreenIds.has(screenCodeToIdMap.HR_DASHBOARD)) {
        return '/hr-dashboard'
      } else if (department === 'accounts' && userScreenIds.has(screenCodeToIdMap.RENT_SCHEDULER)) {
        return '/invoice-schedule'
      } else if (
        department === 'projects' &&
        userScreenIds.has(screenCodeToIdMap.FACILITY_DASHBOARD)
      ) {
        return '/facility-dashboard'
      } else if (department === 'legal' && userScreenIds.has(screenCodeToIdMap.SALES_DASHBOARD)) {
        return '/sales-dashboard'
      }
    }

    // If no specific dashboard found, try to find any dashboard the user has access to
    const accessibleDashboards = [
      { id: screenCodeToIdMap.MARKETING_DASHBOARD, path: '/marketing-dashboard' },
      { id: screenCodeToIdMap.SALES_DASHBOARD, path: '/sales-dashboard' },
      { id: screenCodeToIdMap.OPERATIONS_DASHBOARD, path: '/operations-dashboard' },
      { id: screenCodeToIdMap.FACILITY_DASHBOARD, path: '/facility-dashboard' },
      { id: screenCodeToIdMap.HR_DASHBOARD, path: '/hr-dashboard' },
    ].filter(d => userScreenIds.has(d.id))

    if (accessibleDashboards.length > 0) {
      return accessibleDashboards[0].path
    }

    // If no dashboards are accessible, try to find any accessible screen
    if (userScreens && userScreens.length > 0) {
      // Sort screens by some priority or just take the first one
      const firstAccessibleScreen = userScreens[0]
      // You'll need to map screen_id to actual route here
      return getRouteFromScreenId(firstAccessibleScreen.screen_id)
    }

    // Fallback to not-found if absolutely no screens are accessible
    return '/not-found'
  }

  const getRouteFromScreenId = screenId => {
    // You'll need to create this mapping based on your screens data
    const screenIdToRouteMap = {
      1: '/register-user',
      2: '/add-center',
      3: '/create-sales-rep',
      4: '/create-enquiry',
      5: '/enquiry-reports',
      6: '/create-organisation',
      7: '/site-visits',
      8: '/create-agency',
      9: '/create-agency-rep',
      10: '/create-customer-group',
      11: '/create-contract',
      12: '/contract-reports',
      13: '/book-meeting-room',
      14: '/create-parking',
      15: '/create-day-pass',
      16: '/record-ot',
      17: '/chargeable-customization',
      18: '/record-stationaries',
      19: '/create-ims',
      20: '/create-exit-checklist',
      21: '/create-delivery-challan',
      22: '/create-onboarding',
      23: '/onboardings-reports',
      24: '/invoice-schedule',
      25: '/invoice-schedule-report',
      26: '/create-vendor',
      27: '/create-equipments',
      28: '/create-product-type',
      29: '/maintenance-schedule',
      30: '/maintenance-record',
      31: '/create-employee',
      32: '/employee-details',
      33: '/employee-exit-checklist',
      34: '/employee-exit-checklists-reports',
      35: '/leave-management-v2',
      37: '/marketing-dashboard',
      38: '/sales-dashboard',
      39: '/operations-dashboard',
      40: '/facility-dashboard',
      41: '/hr-dashboard',
      42: '/update-user',
      43: '/meeting-room',
      44: '/meeting-room-credits',
      45: '/client-invoices',
      46: '/vendor-purchase-contract',
      47: '/vendor-purchase-contract-reports',
      48: '/vendor-purchase-contract-schedule',
      49: '/vendor-purchase-contract-schedule-reports',
      50: '/product',
      51: '/vendor-types',
      52: '/units',
      53: '/client-attendance',
      54: '/project-costing',
      55: '/create-opex-checklist',
      56: '/create-opex-workflow',
      57: '/lease-finance-institution',
      58: '/visitor-management',
      59: '/visitor-management-reports',
      60: '/watchman-management',
    }

    return screenIdToRouteMap[screenId] || '/not-found'
  }
  const department = (employee && employee.department?.toLowerCase()) || ''

  useEffect(() => {
    if (loading) return

    const path = getAccessibleDashboard(user.Screens, roleName, department)
    navigate(path, { replace: true })
  }, [roleName, employee, loading, navigate, user])

  return null
}

export default RoleBasedRedirect
